/**
 * BwC Customers Tab Styles
 * Rich dashboard-style design matching the BwC Reporting Dashboard theme
 */

import { mergeStyleSets } from "@fluentui/react";
import { CSSProperties } from "react";

export const BWC_CUSTOMERS_STYLES = mergeStyleSets({
  tabContainer: {
    padding: "0 20px",
    marginTop: "0",
    minHeight: "calc(100vh - 200px)",
    background: "#FFFFFF"
  } as CSSProperties,

  // Stats Tiles - matching screenshot design
  tilesRow: {
    display: "flex",
    gap: "16px",
    padding: "16px 0"
  } as CSSProperties,

  statTile: {
    background: "#F8F9FA",
    borderRadius: "8px",
    padding: "12px 16px",
    display: "flex",
    alignItems: "center",
    gap: "12px",
    border: "1px solid #E6E9ED",
    minWidth: "180px"
  } as CSSProperties,

  statTileIcon: {
    width: "40px",
    height: "40px",
    borderRadius: "8px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "18px",
    flexShrink: 0
  } as CSSProperties,

  iconBlue: {
    background: "#0078D4",
    color: "#FFFFFF"
  } as CSSProperties,

  iconTeal: {
    background: "#00A67E",
    color: "#FFFFFF"
  } as CSSProperties,

  iconGreen: {
    background: "#27ae60",
    color: "#FFFFFF"
  } as CSSProperties,

  iconOrange: {
    background: "#e67e22",
    color: "#FFFFFF"
  } as CSSProperties,

  iconRed: {
    background: "#e74c3c",
    color: "#FFFFFF"
  } as CSSProperties,

  statTileContent: {
    display: "flex",
    flexDirection: "column",
    gap: "0"
  } as CSSProperties,

  statTileValue: {
    fontSize: "24px",
    fontWeight: 700,
    color: "#0078D4",
    lineHeight: 1.2,
    display: "inline"
  } as CSSProperties,

  statTileChange: {
    fontSize: "11px",
    fontWeight: 500,
    color: "#00A67E",
    display: "inline",
    marginLeft: "4px"
  } as CSSProperties,

  statTileLabel: {
    fontSize: "11px",
    color: "#6B7280",
    marginTop: "2px"
  } as CSSProperties,

  // Funnel Card - matching screenshot
  funnelCard: {
    background: "#FFFFFF",
    borderRadius: "8px",
    padding: "16px 20px",
    margin: "0 0 16px 0",
    border: "1px solid #E6E9ED"
  } as CSSProperties,

  funnelCardTitle: {
    fontSize: "14px",
    fontWeight: 600,
    color: "#1C355E",
    marginBottom: "12px",
    display: "flex",
    alignItems: "center"
  } as CSSProperties,

  funnelContainer: {
    display: "flex",
    gap: "12px",
    alignItems: "flex-end",
    padding: "8px 0"
  } as CSSProperties,

  funnelStage: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "6px"
  } as CSSProperties,

  funnelBarWrap: {
    width: "100%",
    background: "#F0F2F5",
    borderRadius: "4px 4px 0 0",
    height: "60px",
    display: "flex",
    alignItems: "flex-end",
    position: "relative"
  } as CSSProperties,

  funnelBar: {
    width: "100%",
    borderRadius: "4px 4px 0 0",
    transition: "height 0.4s ease"
  } as CSSProperties,

  funnelCount: {
    fontSize: "16px",
    fontWeight: 700,
    color: "#1C355E"
  } as CSSProperties,

  funnelLabel: {
    fontSize: "11px",
    color: "#6B7280",
    textAlign: "center"
  } as CSSProperties,

  // Table Card - matching screenshot
  tableCard: {
    background: "#FFFFFF",
    borderRadius: "8px",
    border: "1px solid #E6E9ED",
    overflow: "hidden"
  } as CSSProperties,

  tableHeader: {
    padding: "12px 16px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    borderBottom: "1px solid #E6E9ED",
    background: "#FAFBFC"
  } as CSSProperties,

  tableTitle: {
    fontSize: "16px",
    fontWeight: 700,
    color: "#0f3d6e",
    display: "flex",
    alignItems: "center"
  } as CSSProperties,

  tableToolbar: {
    display: "flex",
    alignItems: "center",
    gap: "12px"
  } as CSSProperties,

  // Loading & Error States
  fullPageLoadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "calc(100vh - 250px)",
    width: "100%"
  } as CSSProperties,

  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "80px 20px"
  } as CSSProperties,

  errorContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "80px 20px",
    textAlign: "center",
    background: "#ffffff",
    borderRadius: "12px",
    margin: "20px",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
    border: "1px solid #e8e8e8"
  } as CSSProperties,

  // Table styling overrides
  tableContainer: {
    padding: "0 20px 20px 20px",
    "& .MuiDataGrid-root": {
      border: "none",
      fontSize: "13px"
    },
    "& .MuiDataGrid-columnHeaders": {
      background: "#f2f6fb",
      color: "#4a5568",
      fontWeight: 700,
      fontSize: "11px",
      textTransform: "uppercase",
      letterSpacing: "0.5px"
    },
    "& .MuiDataGrid-row:hover": {
      background: "#f7fbff"
    },
    "& .MuiDataGrid-cell": {
      padding: "12px 16px",
      borderBottom: "1px solid #f0f0f0"
    }
  } as CSSProperties,

  // Badge styles
  statusBadge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "4px 12px",
    borderRadius: "16px",
    fontSize: "12px",
    fontWeight: 600,
    letterSpacing: "0.3px"
  } as CSSProperties,

  // Link styles
  customerLink: {
    color: "#2980b9",
    fontWeight: 600,
    textDecoration: "none",
    ":hover": {
      textDecoration: "underline"
    }
  } as CSSProperties,

  // Empty state
  emptyState: {
    textAlign: "center",
    padding: "60px 20px",
    color: "#95a5a6"
  } as CSSProperties,

  emptyStateIcon: {
    fontSize: "48px",
    marginBottom: "16px",
    display: "block"
  } as CSSProperties,

  // Filters Section - Blue card matching portal icon style
  filtersCard: {
    background: "linear-gradient(135deg, rgb(0, 90, 158) 0%, rgb(0, 120, 212) 100%)",
    borderRadius: "12px",
    padding: "16px 20px",
    marginBottom: "16px",
    boxShadow: "0 4px 12px rgba(0, 120, 212, 0.25)"
  } as CSSProperties,

  filtersTitle: {
    color: "#FFFFFF",
    fontSize: "14px",
    fontWeight: 600,
    marginBottom: "12px",
    display: "flex",
    alignItems: "center",
    gap: "8px"
  } as CSSProperties,

  filtersRow: {
    display: "flex",
    gap: "16px",
    alignItems: "flex-end",
    flexWrap: "wrap"
  } as CSSProperties,

  filterGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    width: "160px",
    minWidth: "140px",
    flexShrink: 0
  } as CSSProperties,

  filterLabel: {
    color: "#FFFFFF",
    fontSize: "11px",
    fontWeight: 500,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    opacity: 0.9
  } as CSSProperties,

  clearButton: {
    background: "rgba(255, 255, 255, 0.2)",
    color: "#FFFFFF",
    border: "1px solid rgba(255, 255, 255, 0.3)",
    borderRadius: "6px",
    padding: "8px 20px",
    fontSize: "13px",
    fontWeight: 500,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "6px",
    transition: "all 0.2s ease",
    height: "36px",
    flexShrink: 0
  } as CSSProperties,

  // AI Companion Section Styles
  aiCompanionContainer: {
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    borderRadius: "16px",
    padding: "20px 24px",
    marginBottom: "20px",
    boxShadow: "0 8px 32px rgba(102, 126, 234, 0.35)",
    position: "relative",
    overflow: "hidden"
  } as CSSProperties,

  aiCompanionHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "16px"
  } as CSSProperties,

  aiCompanionTitle: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    color: "#FFFFFF",
    fontSize: "18px",
    fontWeight: 700
  } as CSSProperties,

  aiCompanionIcon: {
    width: "40px",
    height: "40px",
    borderRadius: "12px",
    background: "rgba(255, 255, 255, 0.2)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "20px"
  } as CSSProperties,

  aiScenarioTabs: {
    display: "flex",
    gap: "8px",
    marginBottom: "16px"
  } as CSSProperties,

  aiScenarioTab: {
    background: "rgba(255, 255, 255, 0.15)",
    border: "1px solid rgba(255, 255, 255, 0.3)",
    borderRadius: "20px",
    padding: "8px 16px",
    fontSize: "13px",
    fontWeight: 500,
    color: "#FFFFFF",
    cursor: "pointer",
    transition: "all 0.2s ease",
    display: "flex",
    alignItems: "center",
    gap: "6px"
  } as CSSProperties,

  aiScenarioTabActive: {
    background: "rgba(255, 255, 255, 0.95)",
    color: "#667eea",
    border: "1px solid transparent",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)"
  } as CSSProperties,

  aiInsightCard: {
    background: "rgba(255, 255, 255, 0.95)",
    borderRadius: "12px",
    padding: "16px 20px",
    marginBottom: "12px",
    boxShadow: "0 4px 16px rgba(0, 0, 0, 0.1)",
    transition: "transform 0.2s ease, box-shadow 0.2s ease",
    cursor: "pointer"
  } as CSSProperties,

  aiInsightCardHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "12px"
  } as CSSProperties,

  aiInsightCustomerName: {
    fontSize: "16px",
    fontWeight: 600,
    color: "#1A2A45",
    display: "flex",
    alignItems: "center",
    gap: "8px"
  } as CSSProperties,

  aiInsightBadge: {
    display: "inline-flex",
    alignItems: "center",
    padding: "4px 10px",
    borderRadius: "12px",
    fontSize: "11px",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.3px"
  } as CSSProperties,

  aiInsightBadgeHigh: {
    background: "linear-gradient(135deg, #059669 0%, #10b981 100%)",
    color: "#FFFFFF"
  } as CSSProperties,

  aiInsightBadgeMedium: {
    background: "linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)",
    color: "#FFFFFF"
  } as CSSProperties,

  aiInsightBadgeLow: {
    background: "linear-gradient(135deg, #94a3b8 0%, #cbd5e1 100%)",
    color: "#475569"
  } as CSSProperties,

  aiInsightContent: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: "12px"
  } as CSSProperties,

  aiInsightSection: {
    background: "#f8fafc",
    borderRadius: "8px",
    padding: "12px",
    border: "1px solid #e2e8f0"
  } as CSSProperties,

  aiInsightSectionTitle: {
    fontSize: "11px",
    fontWeight: 600,
    color: "#64748b",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    marginBottom: "8px",
    display: "flex",
    alignItems: "center",
    gap: "6px"
  } as CSSProperties,

  aiInsightSectionList: {
    margin: 0,
    paddingLeft: "16px",
    fontSize: "13px",
    color: "#334155",
    lineHeight: 1.6
  } as CSSProperties,

  aiInsightSquadChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    background: "#e0e7ff",
    color: "#3730a3",
    borderRadius: "16px",
    padding: "4px 12px",
    fontSize: "12px",
    fontWeight: 500,
    margin: "2px"
  } as CSSProperties,

  aiInsightSummary: {
    display: "flex",
    alignItems: "center",
    gap: "24px",
    padding: "16px 20px",
    background: "rgba(255, 255, 255, 0.9)",
    borderRadius: "12px",
    marginBottom: "16px"
  } as CSSProperties,

  aiInsightSummaryItem: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center"
  } as CSSProperties,

  aiInsightSummaryValue: {
    fontSize: "28px",
    fontWeight: 700,
    color: "#667eea"
  } as CSSProperties,

  aiInsightSummaryLabel: {
    fontSize: "11px",
    color: "#64748b",
    textTransform: "uppercase",
    letterSpacing: "0.5px"
  } as CSSProperties,

  aiCardsContainer: {
    maxHeight: "400px",
    overflowY: "auto",
    paddingRight: "8px"
  } as CSSProperties,

  aiEmptyState: {
    textAlign: "center",
    padding: "40px 20px",
    color: "rgba(255, 255, 255, 0.8)"
  } as CSSProperties,

  aiEmptyStateIcon: {
    fontSize: "48px",
    marginBottom: "12px",
    opacity: 0.6
  } as CSSProperties
});
